import { useCallback, useEffect, useState } from "react";

import { DEFAULT_NOTE_CHARACTER_LIMIT } from "~/constants";
import { logger } from "~/utils/logging";
import { isAndroid } from "~/utils/helpers";
import { useUserContext } from "~/hooks/useUserContext";
import { useRect } from "~/hooks/useRect";
import { useLabels } from "~/hooks/useLabels";

import { Box } from "../box/Box";
import { Card } from "../card/Card";
import { Text } from "../text/Text";
import { useToasts } from "../toast/useToasts";
import { Icon } from "../icon/Icon";
import { HtmlContent } from "../htmlContent/HtmlContent";
import { RichTextCard } from "../richTextCard/RichTextCard";
import { Button } from "../button/Button";
import { Truncate } from "../truncate/Truncate";

import "./ReflectionCard.css";

import type { CSSProperties, SyntheticEvent, ReactNode } from "react";
import type { TextEditorSaveEvent } from "../textEditor/TextEditor";

export interface ReflectionResponseData {
  value: string;
}

export interface ReflectionCardSaveEvent extends ReflectionResponseData {}

export interface ReflectionCardProps {
  limit?: number;
  placeholder?: string;
  question: string;
  response: string;
  userResponse?: string;
  userResponseHtml?: string;
  onRequestEdit?: () => void;
  onSave?: (event: ReflectionCardSaveEvent) => Promise<void>;
}

const DEFAULT_LABELS = {
  "reflection.answer": "My Response",
  "reflection.bibleProjectAnswer": "BibleProject Response",
  "reflection.addResponse": "Add Your Response To Reveal",
  "textEditor.heading.reflection": "Reflection Question",
  "textEditor.placeholder.reflection": "Write a Response…",
};

const ReflectionQuestionAccordion = ({ children }: { children: ReactNode }) => {
  const [expanded, setExpanded] = useState(false);
  const [requiresExpansion, setRequiresExpansion] = useState(false);
  const [ref, DOMRect] = useRect<HTMLSpanElement>();

  useEffect(() => {
    if (!DOMRect || expanded) return;
    setRequiresExpansion(
      ref.current ? ref.current.clientHeight < ref.current.scrollHeight : false,
    );
  }, [expanded, DOMRect, ref]);

  function handleClick(e: SyntheticEvent) {
    e.preventDefault();
    setExpanded((expanded) => {
      return !expanded;
    });
  }

  return (
    <div
      className="ReflectionQuestionAccordion"
      aria-expanded={expanded}
      role="button"
      onClick={handleClick}
    >
      <Truncate ref={ref} lines={expanded ? 100 : 2}>
        {children}
      </Truncate>

      {requiresExpansion && (
        <Button
          icon="angle-down"
          appearance="minimal"
          shape="circle"
          size="xs"
        />
      )}
    </div>
  );
};

export function ReflectionCard({
  limit = DEFAULT_NOTE_CHARACTER_LIMIT,
  placeholder,
  question,
  response,
  userResponse,
  userResponseHtml,
  onRequestEdit,
  onSave,
}: ReflectionCardProps) {
  const { currentUser, promptLogin } = useUserContext();
  const { showToast } = useToasts();
  const { t } = useLabels({ fallback: DEFAULT_LABELS });

  const customProperties = {
    "--html-content-line-height": "var(--line-height-snug)",
  } as CSSProperties;

  const handleRequestEdit = (e: SyntheticEvent) => {
    if (!currentUser) {
      e.preventDefault();
      promptLogin();
      return;
    }

    onRequestEdit?.();
  };

  const handleSaveResponse = useCallback(
    async (event: TextEditorSaveEvent) => {
      try {
        await onSave?.({ value: event.value });
      } catch (error) {
        showToast({
          intent: "error",
          title: t("reflection.savedToast.error"),
        });
        logger.error("Saving reflection response failed", error);
      }
    },
    [onSave, showToast, t],
  );

  return (
    <div className="ReflectionCard" style={customProperties}>
      <Card>
        <Box gap="var(--size-1-5)" direction="column">
          <div className="ReflectionCard__question">
            <HtmlContent html={question} />
          </div>

          <Box gap="var(--size-1)" direction="column">
            <Text size="sm" weight="semibold">
              {t("reflection.answer")}
              {userResponse && (
                <Icon
                  size="var(--size-1-5)"
                  icon="circle-check"
                  variant="solid"
                  color="var(--color-success)"
                  style={{
                    marginLeft: "var(--size-0-5)",
                    marginBottom: "-1px",
                  }}
                />
              )}
            </Text>

            <RichTextCard
              useSimpleEditor={isAndroid()}
              limit={limit}
              placeholder={placeholder}
              value={userResponse}
              valueHtml={userResponseHtml}
              editorPostTitle={
                <ReflectionQuestionAccordion>
                  <HtmlContent html={question} />
                </ReflectionQuestionAccordion>
              }
              editorTitle={t("textEditor.heading.reflection")}
              onRequestEdit={handleRequestEdit}
              onSave={handleSaveResponse}
            />
          </Box>

          <Box gap="var(--size-1)" direction="column">
            <div>
              <Text size="sm" weight="semibold">
                {t("reflection.bibleProjectAnswer")}
              </Text>
              {!userResponse && (
                <Text
                  size="sm"
                  style={{ fontStyle: "italic" }}
                  color="neutral-50"
                >
                  {t("reflection.addResponse")}
                </Text>
              )}
            </div>
            <div
              className="ReflectionCard__card"
              aria-hidden={Boolean(userResponse)}
              aria-readonly
            >
              {!userResponse && (
                <div className="ReflectionCard__card--scrim"></div>
              )}
              <HtmlContent html={response} />
            </div>
          </Box>
        </Box>
      </Card>
    </div>
  );
}
